import type { Experiment } from 'types';
import { useState } from 'react';
import { SearchInput } from '@/components/SearchInput';
import { cn } from 'helpers/cn';
import { SEARCH_FIELD_CLASS } from '../../ConsolePanel';
import { ExperimentCard } from './ExperimentCard';

type DraftTrayProps = {
  experiments: Experiment[];
  onDropExperiment: (experimentId: number) => void;
};

/**
 * The drafts not yet on the board.
 *
 * Doubles as the place a card lands when dragged off a stage, so removing something is
 * the same gesture as adding it. Only drafts appear: a pipeline exists to start what is
 * on it, and the API refuses anything that is already running or finished.
 */
export const DraftTray = ({ experiments, onDropExperiment }: DraftTrayProps) => {
  const [search, setSearch] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const term = search.trim().toLowerCase();
  const visible = term
    ? experiments.filter(
        (experiment) => experiment.name.toLowerCase().includes(term) || String(experiment.id).includes(term),
      )
    : experiments;

  const handleDrop = (ev: React.DragEvent) => {
    ev.preventDefault();
    setIsDragOver(false);
    const experimentId = Number(ev.dataTransfer.getData('experimentId'));
    if (Number.isInteger(experimentId) && experimentId > 0) onDropExperiment(experimentId);
  };

  return (
    <div
      onDragOver={(ev) => {
        ev.preventDefault();
        ev.dataTransfer.dropEffect = 'move';
      }}
      onDragEnter={() => setIsDragOver(true)}
      onDragLeave={(ev) => {
        if (!ev.currentTarget.contains(ev.relatedTarget as HTMLElement)) setIsDragOver(false);
      }}
      onDrop={handleDrop}
      data-tour="pipelines-drafts"
      className={cn(
        'border-line rounded-panel flex w-[270px] shrink-0 flex-col gap-2.5 border border-dashed p-2.5 transition-colors duration-150',
        isDragOver && 'border-sel-line bg-sel-bg border-solid',
      )}
    >
      <div className="space-y-1">
        <p className="micro-label text-foreground">Unassigned drafts</p>
        <p className="supporting">
          {experiments.length === 0 ? 'Every draft is on the board.' : `${experiments.length} available`}
        </p>
      </div>

      {/* `relative`: SearchInput collapses to a magnifier below `md` and drops its
          field in absolutely, so without a positioned parent the field escapes the
          tray and lands wherever the nearest positioned ancestor happens to be. */}
      {experiments.length > 0 && (
        <div className="relative">
          <SearchInput value={search} handleSearch={setSearch} className={SEARCH_FIELD_CLASS} />
        </div>
      )}

      <div className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
        {visible.length === 0 ? (
          <p className="supporting border-line rounded-card border border-dashed p-3 text-center">
            {experiments.length === 0 ? 'Drag a card here to take it off the board.' : 'Nothing matches that search.'}
          </p>
        ) : (
          visible.map((experiment) => (
            <ExperimentCard
              key={experiment.id}
              experiment={experiment}
              onDragStart={(ev) => {
                ev.dataTransfer.setData('experimentId', String(experiment.id));
                ev.dataTransfer.effectAllowed = 'move';
              }}
            />
          ))
        )}
      </div>
    </div>
  );
};
